import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', type: '' });
  const [selectedContact, setSelectedContact] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [updating, setUpdating] = useState(false);

  const statusOptions = [
    { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'resolved', label: 'Resolved', color: 'bg-green-100 text-green-800' },
    { value: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800' }
  ];

  const typeOptions = [
    { value: 'general', label: 'General' },
    { value: 'admission', label: 'Admission' },
    { value: 'academic', label: 'Academic' },
    { value: 'placement', label: 'Placement' },
    { value: 'research', label: 'Research' },
    { value: 'infrastructure', label: 'Infrastructure' },
    { value: 'feedback', label: 'Feedback' },
    { value: 'technical', label: 'Technical' }
  ];

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: 15, ...filters };
      const response = await apiService.getContacts(params);

      if (response.success) {
        setContacts(response.data.contacts);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError('Failed to fetch contact messages');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Failed to fetch contact messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [currentPage, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
    setCurrentPage(1);
  };

  const openModal = (contact) => {
    setSelectedContact(contact);
    setResponseText(contact.response || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedContact(null);
    setResponseText('');
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      setUpdating(true);
      await apiService.updateContactStatus(id, { status });
      await fetchContacts();
      if (selectedContact?.id === id) {
        setSelectedContact(prev => ({ ...prev, status }));
      }
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  const handleRespond = async () => {
    if (!selectedContact || !responseText.trim()) return;
    try {
      setUpdating(true);
      await apiService.respondToContact(selectedContact.id, responseText);
      await fetchContacts();
      closeModal();
    } catch (err) {
      setError('Failed to send response');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await apiService.deleteContact(id);
      await fetchContacts();
    } catch (err) {
      setError('Failed to delete message');
    }
  };

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    return opt || { label: status, color: 'bg-gray-100 text-gray-800' };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading && contacts.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <i className="fas fa-spinner fa-spin text-2xl text-crimson"></i>
        <span className="ml-2 text-gray-500">Loading contact messages...</span>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-slate">Contact Messages</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <i className="fas fa-exclamation-circle mr-2"></i>{error}
          <button onClick={() => setError('')} className="float-right text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Status</label>
            <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson">
              <option value="">All Statuses</option>
              {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate mb-1">Category</label>
            <select value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson">
              <option value="">All Categories</option>
              {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex items-end">
            <button onClick={() => { setFilters({ status: '', type: '' }); setCurrentPage(1); }}
              className="px-4 py-2 bg-gray-100 text-slate rounded-lg hover:bg-gray-200">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Messages Table */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm overflow-hidden">
        {contacts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <i className="fas fa-inbox text-4xl mb-3"></i>
            <p>No contact messages found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sender</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {contacts.map(contact => {
                    const badge = getStatusBadge(contact.status);
                    return (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-slate">{contact.name}</div>
                          <div className="text-xs text-gray-500">{contact.email}</div>
                          {contact.phone && <div className="text-xs text-gray-400">{contact.phone}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-slate max-w-xs truncate">{contact.subject}</div>
                          <div className="text-xs text-gray-400 max-w-xs truncate">{contact.message}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs font-medium text-gray-600 capitalize">{contact.type}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badge.color}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{formatDate(contact.createdAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button onClick={() => openModal(contact)} className="text-blue-600 hover:text-blue-800" title="View & Respond">
                              <i className="fas fa-eye"></i>
                            </button>
                            {contact.status === 'new' && (
                              <button onClick={() => handleStatusUpdate(contact.id, 'in_progress')} className="text-yellow-600 hover:text-yellow-800" title="Mark In Progress">
                                <i className="fas fa-clock"></i>
                              </button>
                            )}
                            <button onClick={() => handleDelete(contact.id)} className="text-red-500 hover:text-red-700" title="Delete">
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center p-4 border-t">
                <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 text-sm">Previous</button>
                  <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}
                    className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 text-sm">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail / Respond Modal */}
      {isModalOpen && selectedContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 1000 }}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate">Contact Message</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><strong>Name:</strong> {selectedContact.name}</div>
                  <div><strong>Email:</strong> {selectedContact.email}</div>
                  <div><strong>Phone:</strong> {selectedContact.phone || 'N/A'}</div>
                  <div><strong>Category:</strong> <span className="capitalize">{selectedContact.type}</span></div>
                  <div className="col-span-2"><strong>Subject:</strong> {selectedContact.subject}</div>
                  <div className="col-span-2"><strong>Date:</strong> {formatDate(selectedContact.createdAt)}</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-1">Message</label>
                <div className="p-3 bg-white border border-gray-200 rounded-lg text-sm whitespace-pre-wrap">{selectedContact.message}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-1">Status</label>
                <select value={selectedContact.status} onChange={(e) => handleStatusUpdate(selectedContact.id, e.target.value)}
                  disabled={updating}
                  className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson">
                  {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate mb-1">Response</label>
                <textarea value={responseText} onChange={(e) => setResponseText(e.target.value)}
                  rows="4" placeholder="Type your response..."
                  className="w-full px-3 py-2 border border-border_gray rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson" />
              </div>

              {selectedContact.response && selectedContact.respondedAt && (
                <div className="bg-green-50 p-3 rounded-lg text-sm">
                  <p className="font-medium text-green-800 mb-1">Previous Response ({formatDate(selectedContact.respondedAt)})</p>
                  <p className="text-green-700">{selectedContact.response}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t">
              <button onClick={closeModal} className="px-4 py-2 bg-gray-100 text-slate rounded-lg hover:bg-gray-200">Cancel</button>
              <button onClick={handleRespond} disabled={updating || !responseText.trim()}
                className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 disabled:opacity-50 flex items-center gap-2">
                {updating ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-paper-plane"></i>}
                Mark Resolved & Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
